import { AlertTriangle, Check, Grid3x3, Languages, Presentation, Scissors, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AssistantTurn, PdfPage } from "@/lib/api";
import { cn } from "@/lib/utils";

import { LANGUAGE_NAMES } from "./utils";

interface PageChoiceProps {
  turn: AssistantTurn;
  onConfirm: (pages: number[]) => void;
  /** Ask for the same content as a fully translated deck instead. */
  onRebuildAsDeck: () => void;
  isBusy: boolean;
  done: boolean;
}

/**
 * Below this, calling the result "translated" would overstate it badly enough
 * that the deck route is the honest offer. Mirrors COVERAGE_USABLE on the
 * backend.
 */
const COVERAGE_USABLE = 0.9;

/**
 * The page choice for a `propose_pdf_pages` turn.
 *
 * The composer proposes ten pages out of a couple of hundred and says why it
 * picked each one. Reviewing that proposal is the actual task, so the chosen
 * pages are what you see: large, in order, with the reason beside each.
 *
 * The full grid stays one click away, because which pages matter is a judgement
 * call and every pick has to be overridable — but opening a 194-page contact
 * sheet to check a ten-page suggestion was work nobody asked for.
 */
export function PageChoice({ turn, onConfirm, onRebuildAsDeck, isBusy, done }: PageChoiceProps) {
  const [selected, setSelected] = useState<number[]>(turn.suggested_pages ?? []);
  const [browsing, setBrowsing] = useState(false);

  const reasons = new Map((turn.pick_reasoning ?? []).map((item) => [item.page, item.why]));
  const pages = turn.document?.pages ?? [];
  const byNumber = new Map(pages.map((page) => [page.page, page]));
  const chosen = selected
    .map((number) => byNumber.get(number))
    .filter((page): page is PdfPage => Boolean(page));

  const toggle = (page: number) =>
    setSelected((current) =>
      current.includes(page)
        ? current.filter((item) => item !== page)
        : [...current, page].sort((a, b) => a - b),
    );

  const coverage = turn.translation_coverage;
  const language = turn.spec?.target_language;
  // Only a concern when something is actually being translated: an English
  // extract of an English document reaches everything it needs to.
  const translating = Boolean(language) && language !== turn.document?.detected_language;
  const shortfall =
    translating && coverage && coverage.coverage < COVERAGE_USABLE ? coverage : undefined;

  return (
    <div className="space-y-3">
      {shortfall && !done && (
        <section className="border-outcome-escalated/30 bg-outcome-escalated-tint/60 space-y-2 rounded-lg border p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-outcome-escalated mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 space-y-1.5">
              <p className="text-foreground text-sm font-semibold">
                Only {Math.round(shortfall.coverage * 100)}% of this could be translated in place
              </p>
              <p className="text-foreground/85 text-xs leading-5">
                Keeping the layout means translating over the existing text, and only headings and
                captions have room to change — {shortfall.translatable_lines} lines here. The other{" "}
                {shortfall.untranslatable_lines} are body copy set in a designed layout, with no
                space for a longer language, so they would stay as authored. You would get a
                mostly-English document.
              </p>
              <p className="text-foreground/85 text-xs leading-5">
                To have all of it in {(language && LANGUAGE_NAMES[language]) || language}, I can
                rebuild the content as a deck on the VELUX template instead — every word translated,
                on brand, but a new layout rather than this one.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pl-6">
            <Button size="sm" onClick={onRebuildAsDeck} disabled={isBusy}>
              <Presentation className="mr-1.5 h-3.5 w-3.5" />
              Rebuild as a fully translated deck
            </Button>
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted text-xs">
          <span className="text-foreground font-semibold">{selected.length}</span> of {pages.length}{" "}
          pages selected
          {(turn.suggested_pages ?? []).length > 0 && (
            <span className="ml-1">· suggestion pre-ticked, adjust freely</span>
          )}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="ghost" size="sm" disabled={done} onClick={() => setBrowsing((b) => !b)}>
            <Grid3x3 className="mr-1.5 h-3.5 w-3.5" />
            {browsing ? "Show the selection" : `Browse all ${pages.length}`}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={done}
            onClick={() => setSelected(turn.suggested_pages ?? [])}
          >
            Reset to suggestion
          </Button>
          <Button variant="ghost" size="sm" disabled={done} onClick={() => setSelected([])}>
            Clear
          </Button>
        </div>
      </div>

      {browsing ? (
        <div className="grid max-h-[22rem] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4 lg:grid-cols-6">
          {pages.map((page) => (
            <PageTile
              key={page.page}
              page={page}
              why={reasons.get(page.page)}
              selected={selected.includes(page.page)}
              disabled={done}
              onToggle={() => toggle(page.page)}
            />
          ))}
        </div>
      ) : chosen.length === 0 ? (
        <p className="border-border text-muted rounded-lg border border-dashed px-4 py-6 text-center text-xs">
          No pages selected. Reset to the suggestion, or browse all {pages.length} and pick your
          own.
        </p>
      ) : (
        <ol className="max-h-[26rem] space-y-2 overflow-y-auto pr-1">
          {chosen.map((page) => (
            <li
              key={page.page}
              className="border-border bg-card flex items-start gap-3 rounded-lg border p-2"
            >
              <img
                src={page.thumbnail_url}
                alt={`Page ${page.page}`}
                loading="lazy"
                className="bg-surface-muted border-border h-20 w-28 shrink-0 rounded border object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-semibold">
                  Page {page.page}
                  {page.heading ? <span className="font-normal"> — {page.heading}</span> : null}
                </p>
                {/* The reason is the point of the proposal, so it is read on the
                    page rather than hidden behind a hover. */}
                {reasons.get(page.page) && (
                  <p className="text-muted mt-0.5 text-xs leading-5">{reasons.get(page.page)}</p>
                )}
                <p className="text-faint mt-0.5 text-[11px]">
                  {page.word_count} words · {page.translatable_blocks} translatable heading(s)
                </p>
                {page.has_text_in_image && (
                  <p className="text-outcome-escalated mt-1 flex items-center gap-1 text-[11px]">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    Text is baked into the artwork — it will be escalated, not translated.
                  </p>
                )}
              </div>
              {!done && (
                <button
                  type="button"
                  onClick={() => toggle(page.page)}
                  aria-label={`Remove page ${page.page}`}
                  className="text-muted hover:bg-surface-subtle hover:text-foreground rounded p-1 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {!done && (
        <Button onClick={() => onConfirm(selected)} disabled={isBusy || selected.length === 0}>
          {translating ? (
            <Languages className="mr-1.5 h-4 w-4" />
          ) : (
            <Scissors className="mr-1.5 h-4 w-4" />
          )}
          {/* An extract in the document's own language translates nothing, so
              the button must not promise it does. */}
          {isBusy
            ? translating
              ? "Translating…"
              : "Extracting…"
            : `Keep ${selected.length} page${selected.length === 1 ? "" : "s"}${
                translating ? " and translate" : ""
              }`}
        </Button>
      )}
    </div>
  );
}

function PageTile({
  page,
  why,
  selected,
  disabled,
  onToggle,
}: {
  page: PdfPage;
  why: string | undefined;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={onToggle}
          className={cn(
            "group relative overflow-hidden rounded-lg border text-left transition",
            selected ? "border-velux ring-velux/20 ring-2" : "border-border hover:border-velux/40",
            disabled && "cursor-default opacity-70",
          )}
        >
          <img
            src={page.thumbnail_url}
            alt={`Page ${page.page}`}
            loading="lazy"
            className="bg-surface aspect-[3/4] w-full object-cover"
          />
          {selected && (
            <span className="bg-velux text-velux-foreground absolute top-1 right-1 rounded-full p-0.5">
              <Check className="h-3 w-3" />
            </span>
          )}
          {page.has_text_in_image && (
            <span className="bg-outcome-escalated absolute top-1 left-1 rounded-full p-0.5 text-white">
              <AlertTriangle className="h-3 w-3" />
            </span>
          )}
          <span className="text-muted block truncate px-1.5 py-1 text-[10px]">
            p{page.page}
            {page.heading ? ` · ${page.heading}` : ""}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="font-semibold">
          Page {page.page}
          {page.heading ? ` — ${page.heading}` : ""}
        </p>
        <p className="opacity-80">
          {page.word_count} words · {page.translatable_blocks} translatable heading(s)
        </p>
        {page.has_text_in_image && (
          <p className="text-outcome-escalated-tint mt-1">
            Text is baked into the artwork — it will be escalated, not translated.
          </p>
        )}
        {why && <p className="mt-1 opacity-80">Suggested because: {why}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
